import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ScoreBadge, StatusBadge } from "@/components/ScoreBadge";
import {
  ShieldCheck,
  ShieldAlert,
  MapPin,
  Globe,
  GitBranch,
  TriangleAlert,
  ScanSearch,
  RefreshCw,
  Crosshair,
  Radar,
  KeyRound,
} from "lucide-react";

const JURIS = {
  high: { cls: "bg-red-100 text-red-700 border-red-200", label: "High-risk jurisdiction" },
  elevated: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Elevated jurisdiction" },
  standard: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Standard jurisdiction" },
};

const FEED_BAND = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mt-7">
    <h3 className="ek-eyebrow mb-3 flex items-center gap-1">
      <Icon size={12} /> {title}
    </h3>
    {children}
  </div>
);

export const SupplierProfileDrawer = ({ supplierId, onClose }) => {
  const [data, setData] = useState(null);
  const [enrichment, setEnrichment] = useState(null);

  useEffect(() => {
    if (!supplierId) return;
    let active = true;
    setEnrichment(null);
    api
      .get(`/suppliers/${supplierId}/profile`)
      .then(({ data }) => active && setData({ ...data, _for: supplierId }))
      .catch(() => active && setData({ error: true, _for: supplierId }));
    api
      .get(`/intelligence/enrichment/${supplierId}`)
      .then(({ data }) => active && setEnrichment(data))
      .catch(() => active && setEnrichment({ feeds: [] }));
    return () => {
      active = false;
    };
  }, [supplierId]);

  const ready = data && data._for === supplierId;
  const [rescreening, setRescreening] = useState(false);

  const rescreen = async () => {
    setRescreening(true);
    try {
      const { data: res } = await api.post(`/suppliers/${supplierId}/rescreen`);
      setData((prev) => (prev ? { ...prev, sanctions: res.sanctions } : prev));
      toast.success(
        res.sanctions_alert ? "Re-screened — sanctions match flagged" : "Re-screened — no new matches"
      );
    } catch {
      toast.error("Re-screen failed");
    } finally {
      setRescreening(false);
    }
  };

  return (
    <Sheet open={!!supplierId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="supplier-profile-drawer">
        {!ready ? (
          <>
            <SheetTitle className="sr-only">Supplier profile</SheetTitle>
            <p className="mt-10 text-sm text-ek-ink/30">Loading supplier profile…</p>
          </>
        ) : data.error ? (
          <>
            <SheetTitle className="sr-only">Supplier profile</SheetTitle>
            <p className="mt-10 text-sm text-red-500">Failed to load supplier profile.</p>
          </>
        ) : (
          <>
            <SheetHeader>
              <p className="ek-eyebrow">Tier {data.supplier.tier} supplier · due diligence</p>
              <SheetTitle className="font-display text-2xl text-ek-ink">
                {data.supplier.legal_name}
              </SheetTitle>
              <SheetDescription>Supplier ID: {data.supplier.supplier_id}</SheetDescription>
            </SheetHeader>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-ek-ink/65">
              <span className="flex items-center gap-1"><MapPin size={12} /> {data.supplier.country}</span>
              <span className="font-mono">NAICS {data.supplier.industry_naics}</span>
              {data.supplier.duns ? (
                <span className="font-mono">DUNS {data.supplier.duns}</span>
              ) : (
                <span className="italic text-ek-ink/30">no DUNS</span>
              )}
            </div>

            {/* Jurisdiction */}
            <Section icon={Globe} title="Jurisdiction exposure">
              <span
                data-testid="jurisdiction-badge"
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${JURIS[data.jurisdiction.level].cls}`}
              >
                {JURIS[data.jurisdiction.level].label} ({data.jurisdiction.country || "—"})
              </span>
              <p className="mt-2 text-sm text-ek-ink/60">{data.jurisdiction.rationale}</p>
            </Section>

            {/* Premium intelligence-feed enrichment (inline, per supplier) */}
            {enrichment && enrichment.feeds && enrichment.feeds.length > 0 && (
              <Section icon={Radar} title="Intelligence feed enrichment">
                <div className="space-y-2" data-testid="supplier-enrichment">
                  {enrichment.feeds.map((f) => (
                    <div
                      key={f.provider}
                      data-testid={`enrichment-feed-${f.provider}`}
                      className="rounded-xl border border-black/5 bg-white p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-ek-ink">{f.name}</span>
                          {f.status === "mock" && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700" title={f.licence_note || "Mock mode"}>
                              <KeyRound size={9} /> Mock
                            </span>
                          )}
                          {f.status === "live" && (
                            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700">Live</span>
                          )}
                        </div>
                        <span
                          data-testid={`enrichment-value-${f.provider}`}
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-bold ${FEED_BAND[f.band]}`}
                        >
                          {f.value}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-ek-ink/35">{f.metric}</p>
                      <p className="mt-1 text-sm text-ek-ink/60">{f.summary}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-ek-ink/65">
                  Enrichment shown in mock mode — flips to live signals the moment the feed's licence key is added in Integrations.
                </p>
              </Section>
            )}

            {/* Supply-chain intelligence */}
            {data.analytics && (
              <Section icon={Crosshair} title="Supply-chain intelligence">
                <div className="flex items-center gap-3" data-testid="supplier-analytics">
                  <div className="flex h-16 w-16 flex-col items-center justify-center border border-black/10 bg-white">
                    <span className={`font-display text-2xl font-bold ${data.analytics.composite_score >= 70 ? "text-red-600" : data.analytics.composite_score >= 45 ? "text-amber-600" : "text-emerald-600"}`}>
                      {data.analytics.composite_score}
                    </span>
                    <span className="text-[9px] uppercase tracking-wide text-ek-ink/65">composite</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="bg-ek-purple/10 px-2 py-0.5 text-xs font-semibold capitalize text-ek-purple" data-testid="analytics-quadrant">
                      {data.analytics.quadrant}
                    </span>
                    {data.analytics.is_spof && (
                      <span className="bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid="analytics-spof">
                        Single point of failure
                      </span>
                    )}
                    {data.analytics.sanctioned && (
                      <span className="bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Sanctioned</span>
                    )}
                    <span className="bg-ek-canvas px-2 py-0.5 text-xs text-ek-ink/60">
                      {data.analytics.dependents} depend on it · {data.analytics.descendants} downstream
                    </span>
                  </div>
                </div>
                {data.analytics.spof_reason && (
                  <p className="mt-2 text-xs text-ek-ink/65">{data.analytics.spof_reason}</p>
                )}
              </Section>
            )}

            {/* Sanctions screening */}
            <Section icon={ScanSearch} title="Sanctions & watchlist screening">
              {!data.sanctions.screened ? (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="sanctions-unavailable">
                  {data.sanctions.error}
                </p>
              ) : data.sanctions.match_count === 0 ? (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700" data-testid="sanctions-clear">
                  <ShieldCheck size={16} /> No sanctions or watchlist matches found.
                </div>
              ) : (
                <div data-testid="sanctions-hit" className="space-y-2">
                  <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700">
                    <ShieldAlert size={16} /> {data.sanctions.match_count} sanctions/watchlist match
                    {data.sanctions.match_count > 1 ? "es" : ""} found
                  </div>
                  {data.sanctions.matches.map((m) => (
                    <div key={m.id} data-testid="sanctions-match" className="rounded-xl border border-red-100 bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="flex-1 text-sm font-semibold text-ek-ink">{m.caption}</p>
                        <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                          {Math.round(m.score * 100)}%
                        </span>
                      </div>
                      {m.topics?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.topics.map((t) => (
                            <span key={t} className="rounded-full bg-ek-canvas px-2 py-0.5 text-[10px] font-medium text-ek-ink/60">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="mt-2 text-[11px] text-ek-ink/65">
                        Listed on {m.datasets.length} source list{m.datasets.length > 1 ? "s" : ""}: {m.datasets.slice(0, 5).join(", ")}
                        {m.datasets.length > 5 ? "…" : ""}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[11px] text-ek-ink/65">
                  Screened live via OpenSanctions · dataset “{data.sanctions.dataset}”.
                  {data.sanctions.last_screened && (
                    <> · Last screened {new Date(data.sanctions.last_screened).toLocaleString()}</>
                  )}
                </p>
                <button
                  onClick={rescreen}
                  disabled={rescreening}
                  data-testid="rescreen-button"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ek-purple/30 px-2.5 py-1 text-[11px] font-semibold text-ek-purple transition-colors hover:bg-ek-purple/5 disabled:opacity-50"
                >
                  <RefreshCw size={11} className={rescreening ? "animate-spin" : ""} /> Re-screen
                </button>
              </div>
            </Section>

            {/* Dependency */}
            <Section icon={GitBranch} title="Concentration & dependency">
              {data.dependency.is_single_source ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700" data-testid="single-source-flag">
                  <TriangleAlert size={12} /> Single-source dependency
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                  <ShieldCheck size={12} /> Diversified sourcing
                </span>
              )}
              {data.dependency.flags.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {data.dependency.flags.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-ek-ink/70">
                      <TriangleAlert size={14} className="mt-0.5 shrink-0 text-amber-500" /> {f}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-[11px] text-ek-ink/65">
                Parents: {data.dependency.parents.length} · sub-suppliers: {data.dependency.child_count}
              </p>
            </Section>

            {/* Related risks */}
            <Section icon={ShieldAlert} title="Related enterprise risks">
              {data.linked_risks.length === 0 ? (
                <p className="text-sm text-ek-ink/30">No related third-party risks on the register.</p>
              ) : (
                <div className="space-y-2">
                  {data.linked_risks.map((r) => (
                    <div key={r.risk_id} data-testid="linked-risk-row" className="flex items-center justify-between gap-3 rounded-xl border border-black/5 bg-white p-3">
                      <span className="flex-1 truncate text-sm text-ek-ink">{r.title}</span>
                      <ScoreBadge score={r.residual_score} label="R" />
                      <StatusBadge status={r.status} />
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
